import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TextField } from 'components/Fields';
import PasswordStrength, {
  validatePassword,
} from 'components/PasswordStrength';
import { useSearchParams, useSnack, useError } from 'hooks';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { PASSWORD_REGEX } from '../../constants';
import { resetPassword } from 'api/auth';
import { KoobAuthCard, KoobButton } from '@koob/ui';

const ResetPassword = () => {
  const [{ confirmation_token: confirmationToken }] = useSearchParams();
  const { t } = useTranslation(['auth', 'errors']);
  const { notify } = useSnack();
  const { sendErrorSnack } = useError();
  const navigate = useNavigate();

  const handleResetPassword = async (values) => {
    try {
      const data = await resetPassword(values);

      if (data?.errors?.length > 0) {
        throw data?.errors?.[0];
      }

      notify(t('resetPassword.succeeded'));
      navigate('/login');
    } catch (err) {
      sendErrorSnack(err);
    }
  };

  return (
    <KoobAuthCard
      title={t('resetPassword.title')}
      subtitle={t('resetPassword.subtitle')}
    >
      <Formik
        initialValues={{
          password: '',
          passwordConfirmation: '',
          confirmationToken,
        }}
        validationSchema={Yup.object().shape({
          password: Yup.string()
            .required(t('errors:required'))
            .matches(PASSWORD_REGEX, t('errors:passwordInvalidCharacters')),
          passwordConfirmation: Yup.string()
            .oneOf([Yup.ref('password')], t('errors:passwordDoNotMatch'))
            .required(t('errors:required')),
        })}
        validate={validatePassword}
        onSubmit={handleResetPassword}
      >
        {({ isSubmitting, values, touched, errors }) => (
          <Form>
            <div className="space-y-4">
              <div>
                <TextField
                  type="password"
                  label={t('password')}
                  name="password"
                  autoComplete="off"
                />
                <div className={errors?.password && 'pt-12'}>
                  <PasswordStrength
                    password={values.password}
                    isTouched={touched.password}
                  />
                </div>
              </div>
              <TextField
                type="password"
                label={t('passwordConfirmation')}
                name="passwordConfirmation"
              />
            </div>

            <div className="mt-6">
              <KoobButton
                type="submit"
                variant="navy"
                size="lg"
                block
                loading={isSubmitting}
              >
                {t('resetPassword.button')}
              </KoobButton>
            </div>
          </Form>
        )}
      </Formik>
    </KoobAuthCard>
  );
};

export default ResetPassword;
